// ============================================
// APPLICATION - Caja Turnos Hooks
// ============================================
// Módulo nuevo de Caja 360. Coexiste con use-caja.ts (módulo viejo).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cajaTurnosRepository } from '../../infrastructure/http/caja-turnos.repository';
import { queryKeys } from '../query-keys';
import type {
  AbrirTurnoDto,
  ActualizarConfiguracionCajaDto,
  CerrarTurnoDto,
  FiltrosTurnos,
  ForzarCierreTurnoDto,
  RechazarMovimientoDto,
  RegistrarMovimientoDto,
} from '../../domain/dtos';

/**
 * Mi turno abierto (o null). Sin polling por defecto: se consulta al entrar a la pantalla y tras las
 * acciones propias (que invalidan la caché). Cada consulta de un turno con muchas boletas recalcula los
 * totales, así que un intervalo por terminal cargaba el backend. `polling: true` lo refresca cada 30 s
 * mientras la pestaña está enfocada; `refetchOnFocus: true` lo refresca al volver a la ventana (si pasaron
 * más de 15 s), para pantallas con totales como Mi caja.
 */
export const useMiTurno = (options?: { polling?: boolean; refetchOnFocus?: boolean }) => {
  const polling = options?.polling ?? false;
  return useQuery({
    queryKey: queryKeys.caja.turnoActual(),
    queryFn: () => cajaTurnosRepository.obtenerMiTurno(),
    staleTime: 15 * 1000,
    refetchInterval: polling ? 30 * 1000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: options?.refetchOnFocus ?? false,
  });
};

/** Abrir un turno: el propio, o (propietario) el de otro usuario del almacén vía `usuarioId`. */
export const useAbrirTurno = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: AbrirTurnoDto) => cajaTurnosRepository.abrirTurno(dto),
    // onSuccess async + await: TanStack Query mantiene isPending=true (botón
    // deshabilitado) hasta que esta promesa resuelve, así que el formulario
    // de apertura ya tiene el turno nuevo en caché en el mismo instante en
    // que el botón volvería a habilitarse — sin ventana para un doble click
    // que dispare una segunda apertura mientras el refetch está en vuelo.
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.caja.turnoActual() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.caja.all }),
      ]);
    },
  });
};

/** Cerrar un turno (propio) */
export const useCerrarTurno = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ turnoId, dto }: { turnoId: number; dto: CerrarTurnoDto }) =>
      cajaTurnosRepository.cerrarTurno(turnoId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caja.turnoActual() });
      queryClient.invalidateQueries({ queryKey: queryKeys.caja.all });
    },
    // 404: el turnoId que tenía la pantalla (posible resabio de caché) ya no
    // existe para el backend. Se refresca "mi turno" para que la UI vuelva a
    // mostrar el estado real en vez de insistir con un id que no existe.
    onError: (error: any) => {
      if (error?.statusCode === 404) {
        queryClient.invalidateQueries({ queryKey: queryKeys.caja.turnoActual() });
      }
    },
  });
};

/** Configuración de caja del almacén actual (arqueo obligatorio / aprobación de movimientos) */
export const useConfiguracionCaja = () => {
  return useQuery({
    queryKey: queryKeys.caja.configuracionNueva(),
    queryFn: () => cajaTurnosRepository.obtenerConfiguracion(),
    staleTime: 10 * 60 * 1000,
  });
};

/** Movimientos registrados en un turno */
export const useMovimientosDeTurno = (turnoId: number | undefined, page: number = 0) => {
  return useQuery({
    queryKey: queryKeys.caja.movimientosTurno(turnoId ?? 0, page),
    queryFn: () => cajaTurnosRepository.obtenerMovimientosDeTurno(turnoId as number, page),
    enabled: !!turnoId,
    staleTime: 15 * 1000,
  });
};

/** Registrar un movimiento (retiro/gasto/ingreso extra) en el turno abierto */
export const useRegistrarMovimiento = (turnoId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: RegistrarMovimientoDto) =>
      cajaTurnosRepository.registrarMovimiento(turnoId as number, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caja.turnoActual() });
      if (turnoId) {
        // Sin el número de página: invalida todas las páginas de este turno (prefix match).
        queryClient.invalidateQueries({ queryKey: [...queryKeys.caja.all, 'movimientos', turnoId] });
      }
    },
  });
};

/** Anular (rechazar) mi propio movimiento */
export const useAnularMovimiento = (turnoId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (movimientoId: number) => cajaTurnosRepository.anularMovimiento(movimientoId),
    // async + await: la mutación (compartida por todas las filas de la
    // tabla) sigue "pendiente" hasta que la fila anulada ya no ofrezca de
    // nuevo el botón, evitando un segundo click que reintente anular el
    // mismo movimiento mientras el refetch está en vuelo.
    onSuccess: async () => {
      const invalidations = [queryClient.invalidateQueries({ queryKey: queryKeys.caja.turnoActual() })];
      if (turnoId) {
        invalidations.push(queryClient.invalidateQueries({ queryKey: [...queryKeys.caja.all, 'movimientos', turnoId] }));
      }
      await Promise.all(invalidations);
    },
  });
};

// ============================================
// Administración (propietario) — Fase 6B
// ============================================

/** Historial de turnos con filtros (propietario ve todos, vendedor solo los suyos) */
export const useTurnos = (filtros: FiltrosTurnos) => {
  return useQuery({
    queryKey: queryKeys.caja.turnos(filtros),
    queryFn: () => cajaTurnosRepository.listarTurnos(filtros),
    staleTime: 30 * 1000,
  });
};

/** Turnos abiertos del almacén. "En vivo": se refresca solo mientras la pestaña está enfocada. */
export const useTurnosAbiertos = (page: number = 0, sort?: string) => {
  return useQuery({
    queryKey: queryKeys.caja.turnosAbiertos(page, sort),
    queryFn: () => cajaTurnosRepository.listarTurnosAbiertos(page, sort),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
  });
};

/** Detalle de un turno específico (abierto o cerrado) */
export const useTurnoDetalle = (turnoId: number | undefined) => {
  return useQuery({
    queryKey: queryKeys.caja.turnoDetalle(turnoId ?? 0),
    queryFn: () => cajaTurnosRepository.obtenerTurnoPorId(turnoId as number),
    enabled: !!turnoId,
    staleTime: 15 * 1000,
  });
};

/** Movimientos pendientes de aprobación del almacén */
export const useMovimientosPendientes = (page: number = 0, sort?: string) => {
  return useQuery({
    queryKey: queryKeys.caja.movimientosPendientes(page, sort),
    queryFn: () => cajaTurnosRepository.listarMovimientosPendientes(page, sort),
    staleTime: 15 * 1000,
  });
};

/**
 * Hook de polling para contar movimientos de caja pendientes de aprobación
 * (solo propietario — pasar `enabled: false` para cuentas de vendedor, ya
 * que el endpoint exige propietario y no tiene sentido consultarlo).
 * Mismo criterio que usePedidosPendientesCount (ecommerce): refresca cada
 * 2 minutos solo cuando la pestaña está activa.
 */
export const useMovimientosPendientesCount = (enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.caja.movimientosPendientesCount,
    queryFn: () => cajaTurnosRepository.contarMovimientosPendientes(),
    enabled,
    refetchInterval: 2 * 60 * 1000,
    refetchIntervalInBackground: false,
    staleTime: 90 * 1000,
    retry: 1,
    throwOnError: false,
  });
};

/** Movimientos resueltos recientemente (turnos aún abiertos), para poder deshacerlos */
export const useMovimientosResueltos = (page: number = 0, sort?: string) => {
  return useQuery({
    queryKey: queryKeys.caja.movimientosResueltos(page, sort),
    queryFn: () => cajaTurnosRepository.listarMovimientosResueltos(page, sort),
    staleTime: 15 * 1000,
  });
};

/** Auditoría de "deshacer": qué resolución tenía cada movimiento antes y quién la deshizo */
export const useMovimientosDeshacerLog = (page: number = 0, sort?: string) => {
  return useQuery({
    queryKey: queryKeys.caja.movimientosDeshacerLog(page, sort),
    queryFn: () => cajaTurnosRepository.listarMovimientosDeshacerLog(page, sort),
    staleTime: 15 * 1000,
  });
};

/** Cierre forzado de un turno ajeno (propietario) */
export const useForzarCierreTurno = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ turnoId, dto }: { turnoId: number; dto: ForzarCierreTurnoDto }) =>
      cajaTurnosRepository.forzarCierreTurno(turnoId, dto),
    onSuccess: (_data, { turnoId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caja.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.caja.turnoDetalle(turnoId) });
    },
    // Mismo caso que useCerrarTurno: un 404 significa que el turnoId mostrado
    // (posible resabio de caché) ya no existe para el backend.
    onError: (error: any, { turnoId }) => {
      if (error?.statusCode === 404) {
        queryClient.invalidateQueries({ queryKey: queryKeys.caja.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.caja.turnoDetalle(turnoId) });
      }
    },
  });
};

/** Aprobar un movimiento pendiente (propietario) */
export const useAprobarMovimiento = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (movimientoId: number) => cajaTurnosRepository.aprobarMovimiento(movimientoId),
    // async + await: la mutación es una sola instancia compartida por todas
    // las filas de "Pendientes de aprobación" (deshabilita todos los
    // botones mientras está en vuelo). Esperar la invalidación evita que la
    // fila recién aprobada quede clickeable de nuevo antes de que el
    // refetch la saque de la lista.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.caja.all });
    },
  });
};

/** Rechazar un movimiento pendiente (propietario) */
export const useRechazarMovimiento = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ movimientoId, dto }: { movimientoId: number; dto: RechazarMovimientoDto }) =>
      cajaTurnosRepository.rechazarMovimiento(movimientoId, dto),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.caja.all });
    },
  });
};

/** Deshacer una resolución (aprobación/rechazo) y volver el movimiento a PENDIENTE (propietario) */
export const useDeshacerResolucionMovimiento = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (movimientoId: number) => cajaTurnosRepository.deshacerResolucionMovimiento(movimientoId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.caja.all });
    },
  });
};

/** Actualizar la configuración de caja del almacén (propietario) */
export const useActualizarConfiguracionCaja = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: ActualizarConfiguracionCajaDto) => cajaTurnosRepository.actualizarConfiguracion(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caja.configuracionNueva() });
    },
  });
};
